import { useCallback, useState } from 'react';
import { uploadDocument } from 'src/services/WorkDocsService';
import { useWorkDocsAuth } from 'src/hooks/useWorkDocsAuth';
import { useIsMounted } from 'src/hooks/useIsMounted';
import { logger, handleError } from 'src/logger';
import { UPLOAD_MAX_FILE_SIZE_MB } from '../constants/BaseballCardConstants';

/** Per-file upload lifecycle status, keyed by file name in `statusByFile`. */
export type UploadStatus = 'pending' | 'success' | 'error';

export interface FileUploadState {
  status: UploadStatus;
  error?: string;
}

interface UseWorkDocsUploadArgs {
  /**
   * Called after at least one file uploads successfully, with the folder it landed in
   * (used to refresh the browser listing). May be async; the hook awaits it, then clears
   * that folder's succeeded statuses so success messages disappear once the refreshed
   * listing shows the files. Failed-file statuses are kept for retry.
   */
  onUploaded?: (parentFolderId: string) => void | Promise<void>;
}

interface UseWorkDocsUploadReturn {
  /** Per-folder upload status: outer key = folder id, inner key = file name. */
  statusByFolder: Map<string, Map<string, FileUploadState>>;
  isUploading: boolean;
  uploadFiles: (parentFolderId: string, files: File[]) => Promise<void>;
  /** Clears one folder's statuses. With `keepErrors`, only failed-file entries are retained. */
  clearFolderStatus: (folderId: string, opts?: { keepErrors?: boolean }) => void;
  reset: () => void;
}

const MAX_BYTES = UPLOAD_MAX_FILE_SIZE_MB * 1024 * 1024;

/**
 * Reads a File into a base64 string (no data-URL prefix), suitable for the upload
 * endpoint's `content` field. Uses FileReader so large files stream in the browser.
 */
const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // result is a data URL: "data:<mime>;base64,<payload>" — keep only the payload.
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });

/**
 * Uploads user-selected files into a chosen WorkDocs folder. Tracks each file's status
 * (`pending` -> `success`/`error`) by name so the UI can show per-file StatusIndicators.
 * Files over the size limit fail locally without a network call. On each success the
 * `onUploaded` callback fires so the caller can refresh the folder listing.
 *
 * All side-effects live here; the calling component stays presentation-only.
 */
export const useWorkDocsUpload = ({ onUploaded }: UseWorkDocsUploadArgs = {}): UseWorkDocsUploadReturn => {
  const { accessToken } = useWorkDocsAuth();
  const isMounted = useIsMounted();

  const [statusByFolder, setStatusByFolder] = useState<Map<string, Map<string, FileUploadState>>>(new Map());
  const [isUploading, setIsUploading] = useState(false);

  // Writes one file's state under its folder, cloning both map levels so React re-renders.
  const setFileState = useCallback(
    (folderId: string, name: string, state: FileUploadState) => {
      if (!isMounted.current) return;
      setStatusByFolder((prev) => {
        const next = new Map(prev);
        const folderMap = new Map(next.get(folderId) ?? []);
        folderMap.set(name, state);
        next.set(folderId, folderMap);
        return next;
      });
    },
    [isMounted]
  );

  const uploadFiles = useCallback(
    async (parentFolderId: string, files: File[]) => {
      if (!accessToken) {
        logger.warn('Baseball Cards: upload attempted without a WorkDocs token');
        return;
      }
      if (files.length === 0) return;

      setIsUploading(true);
      let anySucceeded = false;

      // Upload sequentially: keeps memory bounded for large files and avoids hammering
      // the backend's Zocalo upload flow with parallel presigned PUTs.
      for (const file of files) {
        setFileState(parentFolderId, file.name, { status: 'pending' });

        if (file.size > MAX_BYTES) {
          setFileState(parentFolderId, file.name, { status: 'error', error: `Exceeds ${UPLOAD_MAX_FILE_SIZE_MB} MB limit` });
          continue;
        }

        try {
          const base64Content = await fileToBase64(file);
          const contentType = file.type || 'application/octet-stream';
          await uploadDocument(parentFolderId, file.name, contentType, base64Content, accessToken);
          setFileState(parentFolderId, file.name, { status: 'success' });
          anySucceeded = true;
        } catch (error) {
          handleError('Baseball Cards: WorkDocs upload failed', error);
          setFileState(parentFolderId, file.name, { status: 'error', error: 'Upload failed. Try again.' });
        }
      }

      if (isMounted.current) setIsUploading(false);

      if (anySucceeded) {
        // Refresh (re-list the folder) so the just-uploaded files appear in the listing. We do NOT
        // clear the per-file statuses afterward — the success message stays visible so the user
        // gets lasting confirmation the upload landed (errors likewise persist for retry).
        await onUploaded?.(parentFolderId);
      }
    },
    [accessToken, isMounted, setFileState, onUploaded]
  );

  // Clears one folder's statuses. With `keepErrors`, drops only succeeded/pending entries so
  // failed files stay visible for retry; the folder key is removed entirely if nothing remains.
  const clearFolderStatus = useCallback((folderId: string, opts?: { keepErrors?: boolean }) => {
    setStatusByFolder((prev) => {
      if (!prev.has(folderId)) return prev;
      const next = new Map(prev);
      if (opts?.keepErrors) {
        const kept = new Map(Array.from(next.get(folderId) ?? []).filter(([, state]) => state.status === 'error'));
        if (kept.size > 0) {
          next.set(folderId, kept);
        } else {
          next.delete(folderId);
        }
      } else {
        next.delete(folderId);
      }
      return next;
    });
  }, []);

  const reset = useCallback(() => {
    setStatusByFolder(new Map());
  }, []);

  return { statusByFolder, isUploading, uploadFiles, clearFolderStatus, reset };
};
